<template>
    <v-container class="pa-5" fluid>
        <page-title>Zatwierdzanie raportów / Raporty szczegółowe</page-title>

        <v-row align="center">
            <v-col class="pb-0">
                <v-btn
                    text
                    color="primary"
                    class="pa-0"
                    @click="close"
                >
                    <v-icon color="primary">
                        mdi-arrow-left
                    </v-icon>
                    Powrót do listy
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-container fluid class="pa-0">
                    <v-row justify="end" dense>
                        <v-col cols="12" sm="5" md="4" lg="3" xl="2">
                            <v-text-field
                                v-model="filters.search"
                                append-icon="mdi-magnify"
                                label="Szukaj..."
                                single-line
                                clearable
                                hide-details
                                solo
                                @keyup.enter="changeFilter"
                                @click:append="changeFilter"
                                @click:clear="clearFilter('search')"
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-data-table
                        :headers="headers"
                        :items="detailedShipmentsReports"
                        :loading="detailedShipmentsReportsLoading"
                        :footer-props="footerProps"
                        :server-items-length="pagination.itemsLength"
                        :page.sync="pagination.page"
                        :items-per-page.sync="pagination.itemsPerPage"
                        :mobile-breakpoint="0"
                        name="detailedShipmentsReportsData"
                        @pagination="updatePagination"
                    >
                        <template #item.orderProductionNumber="{item}">
                            <template v-if="item.orderProductionNumber">
                                {{ item.orderProductionNumber }}
                            </template>
                            <template v-else>
                                -
                            </template>
                        </template>
                        <template #item.user="{item}">
                            <template v-if="item.user">
                                <template v-if="item.user.firstName && item.user.lastName">
                                    {{ item.user.firstName }} {{ item.user.lastName }}
                                </template>
                                <template v-else>
                                    {{ item.user.username }}
                                </template>
                            </template>
                            <template v-else>
                                -
                            </template>
                        </template>
                        <template #item.shippedBoxes="{item}">
                            {{ item.shippedBoxes }}
                        </template>
                        <template #item.shippedWeight="{item}">
                            {{ formatWeight(item.shippedWeight, false) }}
                        </template>
                        <template #item.start="{item}">
                            {{ formatDate(item.start) }}
                        </template>
                        <template #item.finish="{item}">
                            {{ formatDate(item.finish) }}
                        </template>
                        <template #item.duration="{item}">
                            {{ parseDuration(item.duration) }}
                        </template>
                        <template #item.actions="{item}">
                            <div class="d-inline-flex">
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Szczegóły raportu"
                                            v-on="on"
                                            @click="openReport(item.id)"
                                        >
                                            <v-icon color="blue">
                                                mdi-ballot
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Szczegóły raportu</span>
                                </v-tooltip>
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import store from '@/./store'
import PageTitle from '@/components/common/PageTitle'
import filters from '@/mixins/dataTableFilters'
import pagination from '@/mixins/dataTablePagination'
import dateFormatter from '@/mixins/dateFormatter'
import parse from '@/mixins/parse.js'
import tooltipDelay from '@/mixins/tooltip'
import weightFormatter from '@/mixins/weightFormatter'
import { errorHandler } from '@/utils/errorHandler'

export default {
    name: 'DetailedShipmentsReports',
    components: {
        PageTitle
    },
    mixins: [
        dateFormatter,
        filters,
        pagination,
        parse,
        tooltipDelay,
        weightFormatter
    ],
    beforeRouteEnter(to, from, next) {
        store.dispatch('reports/getDetailedShipmentsReports', {
            shipmentReportShiftSummary_id: to.params.id,
            paginate: '?limit=10'
        })
            .then(response => {
                next(vm => {
                    vm.handlePaginationResponse(response)
                    vm.shipmentReportShiftSummaryId = to.params.id
                })
            })
            .catch(error => {
                errorHandler(error)
                next(error)
            })
    },
    beforeRouteLeave(to, from, next) {
        if (!this.filtersCleared) {
            this.$store.dispatch('reports/clearDetailedShipmentsReportsFilters')
        }
        this.resetFiltersCleared()
        next()
    },
    data() {
        return {
            shipmentReportShiftSummaryId: null,
            headers: [
                {
                    text: 'Numer',
                    value: 'orderProductionNumber',
                    sortable: false,
                },
                {
                    text: 'Użytkownik',
                    value: 'user',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Ilość kartonów',
                    value: 'shippedBoxes',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Waga sumaryczna',
                    value: 'shippedWeight',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Data rozpoczęcia',
                    value: 'start',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Data zakończenia',
                    value: 'finish',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Czas trwania',
                    value: 'duration',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100
                }
            ]
        }
    },
    computed: {
        detailedShipmentsReports() {
            return this.$store.state.reports.reports.detailedShipmentsReports
        },
        detailedShipmentsReportsLoading() {
            return this.$store.state.reports.reports.detailedShipmentsReportsLoading
        },
        savedFilters() {
            return this.$store.state.reports.reports.detailedShipmentsReportsFilters
        },
        filterQuery() {
            const filters = Object.assign({}, this.filters)
            let query = ''

            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        },
    },
    methods: {
        getDetailedShipmentsReports() {
            this.$store.dispatch('reports/getDetailedShipmentsReports', {
                shipmentReportShiftSummary_id: this.shipmentReportShiftSummaryId,
                paginate: this.paginate(),
                query: this.filterQuery
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        openReport(reportId) {
            this.savePagination(this.$options.name)
            this.saveFiltersCleared()
            this.$router.push({
                name: 'reportsShipmentsReportsShiftSummariesShipmentReport',
                params: {
                    id: reportId
                }
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        close() {
            this.$router.push({ name: 'reportsReportsShiftSummaries' })
                .catch(error => {
                    errorHandler(error)
                })
        },
        changeFilter() {
            this.$store.dispatch('reports/setDetailedShipmentsReportsFilters', this.filters)
                .catch(error => {
                    errorHandler(error)
                })
            this.resetPagination()
            this.getDetailedShipmentsReports()
        },
        updatePagination(pagination) {
            this.pagination = pagination
            this.evaluateSavedPagination(this.$options.name)

            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending && this.shipmentReportShiftSummaryId) {
                this.getDetailedShipmentsReports()
            }
            this.clearPaginationPending()
        }
    },
}

</script>